import type { IndexAttributeItem } from "@/repo";
import {
    createMapboxFillAndOpacity,
    TOKEN_DISTRIBUTION_LEVEL,
} from "@/constants";
import ControlPanelListSearch from "./ControlPanelListSearch";
import { useEffect, useMemo, useState, type RefObject } from "react";
import "./ControlPanelList.css";
import "./ControlPanelSelectedEntity.css";
import type { Map } from "mapbox-gl";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getCellAttributeDistribution } from "@/repo/attribute";
import AttributeDistributionChart from "../AttributeDistributionChart";
import { useNavigate, useParams } from "react-router-dom";
import { getEnvParam, type TileEnv } from "@/env";

interface LocationAttributeListProps {
    attributes: IndexAttributeItem[];
    map: RefObject<Map | undefined>;
    env: TileEnv;
}

function toSafeColumn(name: string): string {
    return name.replace(/[^A-Za-z0-9_]/g, "");
}

function applyLayer(map: Map, attributeName: string) {
    if (map.getSource("mvt-tiles")) {
        if (map.getLayer("tiles")) map.removeLayer("tiles");
        if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
        map.removeSource("mvt-tiles");
    }

    map.addSource("mvt-tiles", {
        type: "vector",
        tiles: [
            `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/attributes/${attributeName}/{z}/{x}/{y}?${getEnvParam()}`,
        ],
    });

    map.addLayer({
        id: "tiles",
        type: "fill",
        source: "mvt-tiles",
        "source-layer": "values",
        paint: createMapboxFillAndOpacity(["get", "value"]),
    });

    map.addLayer({
        id: "tiles-values",
        type: "symbol",
        source: "mvt-tiles",
        "source-layer": "values",
        minzoom: 12.5,
        paint: {
            "text-color": "#fff",
            "text-opacity": [
                "interpolate",
                ["linear"],
                ["get", "value"],
                0,
                0.4,
                1,
                0.6,
            ],
        },
        layout: {
            "text-field": ["get", "value"],
            "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
            "text-offset": [0, 0],
            "text-size": 8,
            "text-anchor": "center",
        },
    });
}

interface SelectedAttributeProps {
    attribute: IndexAttributeItem;
    map: RefObject<Map | undefined>;
    onClose(): void;
    env: TileEnv;
}

function SelectedAttribute({ attribute, map, onClose, env }: SelectedAttributeProps) {
    useEffect(() => {
        const m = map.current;
        if (!m) return;
        applyLayer(m, attribute.name);
        return () => {
            if (m.getLayer("tiles-values")) m.removeLayer("tiles-values");
            if (m.getLayer("tiles")) m.removeLayer("tiles");
            if (m.getSource("mvt-tiles")) m.removeSource("mvt-tiles");
        };
    }, [attribute.name, map, env]);

    const distributionQuery = useQuery({
        queryKey: [
            "cell-attribute-distribution",
            TOKEN_DISTRIBUTION_LEVEL,
            attribute.name,
        ],
        queryFn: () =>
            getCellAttributeDistribution(
                toSafeColumn(attribute.name),
                TOKEN_DISTRIBUTION_LEVEL,
            ),
    });

    return (
        <div className="selected-entity">
            <div className="selected-entity-title">
                <button
                    className="selected-entity-title__exit"
                    onClick={onClose}
                >
                    ←
                </button>
                <div className="selected-entity-title__main">
                    <h3 className="selected-entity-title__name">
                        {attribute.name}
                    </h3>
                    <span className="selected-entity-title__id">
                        ID: {attribute.id}
                    </span>
                </div>
            </div>
            <div className="selected-entity-content">
                <h4>Distribution (level {TOKEN_DISTRIBUTION_LEVEL})</h4>
                {distributionQuery.isPending ? (
                    <span>Loading distribution...</span>
                ) : distributionQuery.isError ? (
                    <span>Failed to load distribution</span>
                ) : (
                    <AttributeDistributionChart data={distributionQuery.data} />
                )}
            </div>
        </div>
    );
}

function LocationAttributeList(props: LocationAttributeListProps) {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const selectedId = id ? Number(id) : undefined;

    const attributes = useMemo(() => {
        return props.attributes.filter((attribute) =>
            attribute.name
                .toLowerCase()
                .trim()
                .includes(search.toLowerCase().trim()),
        );
    }, [props.attributes, search]);

    const selectedAttribute = props.attributes.find((a) => a.id === selectedId);

    if (selectedAttribute) {
        return (
            <SelectedAttribute
                attribute={selectedAttribute}
                map={props.map}
                onClose={() => navigate("/location/attributes/token")}
                env={props.env}
            />
        );
    }

    return (
        <>
            <ControlPanelListSearch
                type="attribute"
                search={search}
                setSearch={setSearch}
            />
            <div className="control-panel-button-list">
                {attributes.map((attribute) => (
                    <button
                        key={attribute.id}
                        onClick={() => {
                            setSearch("");
                            navigate(`/location/attributes/token/${attribute.id}`);
                        }}
                        className={classNames(
                            "control-panel-button-list__item",
                            attribute.id === selectedId &&
                                "control-panel-button-list__item--active",
                        )}
                    >
                        <div></div>
                        {attribute.name}
                    </button>
                ))}
            </div>
        </>
    );
}

export default LocationAttributeList;
